// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import { arrForEach, arrIndexOf, hasOwnProperty, isFunction, isObject, isString, isSymbol, objKeys } from "@microsoft/applicationinsights-core-js";
import { Util } from "@microsoft/applicationinsights-common";
import { strShimPrototype } from "@microsoft/applicationinsights-shims";
var strConstructor = "constructor";
var strGetOwnPropertyNames = "getOwnPropertyNames";
export var MAX_DEPTH = 16;
export function makeRegex(value) {
    if (value && value.length > 0) {
        value = value.replace(/\\/g, "\\\\");
        // eslint-disable-next-line security/detect-non-literal-regexp
        value = value.replace(/([\+\?\|\{\[\(\)\^\$\#\.]}])/g, "\\$1");
        value = value.replace(/\*/g, ".*");
        return new RegExp("(" + value + ")");
    }
    return null;
}
export function toggleClassName(el, className) {
    var idx = el.className.indexOf(className);
    if (idx === -1) {
        el.className += className;
    }
    else {
        el.className = el.className.substring(0, idx) + el.className.substring(idx + className.length);
    }
}
export function traverseAndReplace(target, maxDepth, currentDepth, thingsReferenced, excludedKeys, includeFunctions) {
    var out = {};
    if (!thingsReferenced) {
        thingsReferenced = [];
    }
    if (isObject(target)) {
        for (var _i = 0, _a = getTargetKeys(target, excludedKeys, includeFunctions); _i < _a.length; _i++) {
            var key = _a[_i];
            var targetValue = target[key];
            if (isSymbol(targetValue)) {
                targetValue = targetValue.toString();
            }
            if (targetValue !== null && arrIndexOf(thingsReferenced, targetValue) !== -1) {
                out[key] = "<circular (" + key + " - \"" + getTargetName(targetValue) + "\")>";
            }
            else if (targetValue !== null && isObject(targetValue)) {
                if (currentDepth >= maxDepth) {
                    out[key] = "<max allowed depth reached>";
                }
                else {
                    thingsReferenced.push(target);
                    out[key] = traverseAndReplace(targetValue, maxDepth, currentDepth + 1, thingsReferenced, excludedKeys, includeFunctions);
                    thingsReferenced.pop();
                }
            }
            else {
                out[key] = targetValue;
            }
        }
    }
    return out;
}
function _sanitizeText(value) {
    if (value) {
        value = value.replace(/&/g, "&amp;");
        value = value.replace(/>/g, "&gt;");
        value = value.replace(/</g, "&lt;");
    }
    return value;
}
function _setInnerText(elm, theText, textFilter) {
    var innerText = theText;
    var matchPos = -1;
    var matchLen = 0;
    var rg = makeRegex(textFilter);
    if (rg) {
        var matchTxt = rg.exec(innerText);
        if (matchTxt && matchTxt[1]) {
            matchPos = theText.indexOf(matchTxt[1]);
            matchLen = matchTxt[1].length;
        }
    }
    if (matchPos !== -1) {
        var innerHtml = _sanitizeText(theText.substring(0, matchPos)) +
            "<span class=\"matched-text-filter\">" +
            _sanitizeText(theText.substring(matchPos, matchPos + matchLen)) +
            "</span>" +
            theText.substring(matchPos + matchLen);
        elm.innerHTML = innerHtml;
        return true;
    }
    elm.innerText = theText;
    return false;
}
var lastSelectedElement;
var selectedObject;
export function copySelectedTree() {
    var toCopy = selectedObject;
    if (!toCopy) {
        return;
    }
    var textArea = document.createElement("textarea");
    textArea.innerText = JSON.stringify(toCopy);
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    textArea.parentElement.removeChild(textArea);
}
export function focusHandler(evt, target, level, excludeKeys, includeFunctions) {
    if (lastSelectedElement) {
        toggleClassName(lastSelectedElement, " last-selected-element");
    }
    lastSelectedElement = evt.target;
    for (var i = 0; i < 10; i++) {
        if (lastSelectedElement.tagName === "DIV") {
            break;
        }
        lastSelectedElement = lastSelectedElement.parentElement;
    }
    lastSelectedElement.className += " last-selected-element";
    selectedObject = traverseAndReplace(target, MAX_DEPTH, level, null, excludeKeys, includeFunctions);
}
function _navHandler(evt, openHandler, currentState) {
    var el = evt.target;
    switch (evt.which) {
        // Enter
        case 13:
            (openHandler) ? openHandler(evt) : void 0;
            break;
        // ArrowUp
        case 38:
            evt.preventDefault();
            var prev = el.previousElementSibling;
            if (prev && prev.tagName !== "BUTTON") {
                prev.focus();
            }
            break;
        // ArrowDown
        case 40:
            evt.preventDefault();
            var next = el.nextElementSibling;
            if (next) {
                next.focus();
            }
            break;
        // ArrowRight
        case 39:
            if (openHandler) {
                openHandler(evt, true);
                if (currentState) {
                    el.firstElementChild.nextSibling.focus();
                }
            }
            break;
        // ArrowLeft
        case 37:
            if (openHandler) {
                openHandler(evt, false);
            }
            if (!currentState) {
                el.parentElement.focus();
            }
            break;
        // c
        case 67:
            if (evt.ctrlKey) {
                copySelectedTree();
                evt.target.focus();
            }
            break;
    }
}
export function getTargetName(target) {
    if (target) {
        if (isString(target.identifier)) {
            return target.identifier;
        }
        if (isString(target.name)) {
            return target.name;
        }
        if (hasOwnProperty(target, strShimPrototype)) {
            // Look like a prototype
            return target.name || "";
        }
        return ((target[strConstructor]) || {}).name || "";
    }
    return "";
}
function _toString(value) {
    if (isString(value)) {
        return value;
    }
    if (isSymbol(value)) {
        return value.toString();
    }
    if (isFunction(value["toString"])) {
        return value["toString"]() || "";
    }
    return "";
}
export function getTargetKeys(target, excludedKeys, includeFunctions) {
    var keys = objKeys(target);
    if (!Util.isArray(target)) {
        try {
            if (Object[strGetOwnPropertyNames]) {
                // We need to use this for built in objects such as Error which don't return their values via objKeys because they are not enumerable for example
                var propKeys = Object[strGetOwnPropertyNames](target);
                if (propKeys) {
                    arrForEach(propKeys, function (key) {
                        var theKey = _toString(key);
                        if (theKey && keys.indexOf(theKey) === -1) {
                            keys.push(key);
                        }
                    });
                }
            }
        }
        catch (ex) {
            // getOwnPropertyNames can fail in ES5, if the argument to this method is not an object (a primitive),
            // then it will cause a TypeError. In ES2015, a non-object argument will be coerced to an object.
        }
    }
    var theKeys = [];
    arrForEach(keys, function (key) {
        if (!includeFunctions && isFunction(target[key])) {
            return;
        }
        var theKey = _toString(key);
        if (theKey && excludedKeys.indexOf(theKey) === -1) {
            theKeys.push(theKey);
        }
    });
    return theKeys;
}
export function formatLogElements(target, tmLabel, key, level, textFilter, excludeKeys, thingsReferenced, includeFunctions) {
    var openState = false;
    if (!level) {
        level = 0;
    }
    if (!thingsReferenced) {
        thingsReferenced = [];
    }
    var isObj = isObject(target) || Util.isError(target);
    var isErr = target["baseType"] === "ExceptionData" || Util.isError(target);
    var children = [];
    function _openNode(currentLine) {
        openState = true;
        arrForEach(children, function (child) {
            rootDiv.appendChild(child);
        });
        currentLine.className = "obj-key expandable open";
    }
    function _collapseNode(currentLine) {
        // rootDiv.innerHTML = '';
        arrForEach(children, function (child) {
            rootDiv.removeChild(child);
        });
        // rootDiv.appendChild(currentLine);
        openState = false;
        currentLine.className = "obj-key expandable closed";
    }
    var matched = false;
    var childOpened = false;
    var keys = getTargetKeys(target, excludeKeys, includeFunctions);
    if (keys.length === 0) {
        keys.push("<empty>");
    }
    if (level >= MAX_DEPTH) {
        keys.unshift("<maxdepth>");
    }
    for (var _i = 0, keys_1 = keys; _i < keys_1.length; _i++) {
        var key_1 = keys_1[_i];
        if (excludeKeys.indexOf(key_1) !== -1) {
            continue;
        }
        var targetValue = target[key_1];
        if (isSymbol(targetValue)) {
            targetValue = targetValue.toString();
        }
        if (key_1 === "<maxdepth>") {
            var builder = document.createElement("div");
            builder.className = "empty";
            builder.innerText = "<max allowed depth reached>";
            children.push(builder);
            break;
        }
        else if (key_1 === "<empty>") {
            var builder = document.createElement("div");
            builder.className = "empty";
            builder.innerText = "<empty>";
            children.push(builder);
        }
        else if (targetValue !== null && arrIndexOf(thingsReferenced, targetValue) !== -1) {
            var builder = document.createElement("div");
            builder.className = "empty";
            builder.innerText = "<circular (" + key_1 + ") - \"" + getTargetName(targetValue) + "\">";
            children.push(builder);
        }
        else if (targetValue !== null && (isObject(targetValue) || Util.isError(targetValue))) {
            thingsReferenced.push(target);
            var formatted = formatLogElements(targetValue, null, key_1, level + 1, textFilter, excludeKeys, thingsReferenced, includeFunctions);
            thingsReferenced.pop();
            if (formatted.matched) {
                childOpened = true;
            }
            if (formatted.isErr) {
                isErr = true;
            }
            children.push(formatted.root);
        }
        else {
            var builder = document.createElement("div");
            builder.setAttribute("tabindex", "0");
            builder.onclick = function (evt) {
                evt.stopPropagation();
            };
            builder.ontouchend = function (evt) {
                evt.stopPropagation();
            };
            builder.onkeydown = function (evt) {
                evt.stopPropagation();
                _navHandler(evt);
            };
            builder.onfocus = function (evt) {
                focusHandler(evt, target, level, excludeKeys, includeFunctions);
            };
            var outerSpan = document.createElement("span");
            var keySpan = document.createElement("span");
            keySpan.className = "key";
            if (_setInnerText(keySpan, key_1 + ": ", textFilter)) {
                childOpened = true;
            }
            outerSpan.appendChild(keySpan);
            var valueSpan = document.createElement("span");
            if (isFunction(targetValue)) {
                var fnStr = targetValue.toString();
                var fnHead = fnStr.match(/^([^{]+)/)[1];
                valueSpan.textContent = fnHead + "{...}";
            }
            else {
                if (_setInnerText(valueSpan, "" + targetValue, textFilter)) {
                    childOpened = true;
                }
            }
            valueSpan.className = "" + typeof targetValue;
            outerSpan.appendChild(valueSpan);
            builder.appendChild(outerSpan);
            children.push(builder);
        }
    }
    var rootDiv = document.createElement("div");
    var innerText = "";
    var currentLine = document.createElement("span");
    if (isObj || children.length) {
        innerText = (key ? key : "obj") + ": ";
        if (Util.isArray(target)) {
            innerText += "[" + getTargetKeys(target, excludeKeys, includeFunctions).length + "]";
        }
        else {
            var targetName = getTargetName(target);
            if (targetName) {
                innerText += " <\"" + targetName + "\"> ";
            }
            innerText += "{" + getTargetKeys(target, excludeKeys, includeFunctions).length + "}";
        }
        matched = _setInnerText(currentLine, innerText, textFilter);
        if (tmLabel) {
            var tmWrapper = document.createElement("span");
            var tmDetails = document.createElement("span");
            tmDetails.className = "obj-time";
            tmDetails.innerText = tmLabel;
            tmWrapper.appendChild(tmDetails);
            tmWrapper.appendChild(currentLine);
            currentLine = tmWrapper;
        }
        currentLine.className = "obj-key expandable closed";
    }
    else {
        innerText = (key ? key : "obj") + ": " + target.toString();
        matched = _setInnerText(currentLine, innerText, textFilter);
        currentLine.className = "obj-key";
    }
    rootDiv.appendChild(currentLine);
    rootDiv.setAttribute("tabindex", "0");
    if (childOpened) {
        // A child node matched so auto-expand
        _openNode(currentLine);
    }
    if (isObj) {
        if (isErr) {
            rootDiv.className = "exception";
        }
        var openHandler_1 = function (evt, forceState) {
            evt.stopPropagation();
            if (Util.getIEVersion()) {
                focusHandler(evt, target, level, excludeKeys, includeFunctions);
            }
            if (forceState !== undefined && openState === forceState) {
                return;
            }
            if (lastSelectedElement === rootDiv) {
                if (openState) {
                    _collapseNode(currentLine);
                }
                else {
                    _openNode(currentLine);
                }
            }
        };
        rootDiv.onkeydown = function (evt) {
            _navHandler(evt, openHandler_1, openState);
        };
        rootDiv.onclick = function (evt) {
            openHandler_1(evt);
        };
        rootDiv.ontouchend = function (evt) {
            openHandler_1(evt);
        };
        rootDiv.onfocus = function (evt) {
            focusHandler(evt, target, level, excludeKeys, includeFunctions);
        };
    }
    return {
        root: rootDiv,
        isErr: isErr,
        matched: matched || childOpened
    };
}
//# sourceMappingURL=helpers.js.map